import { useEffect, FC } from 'react'
import { closeModal } from 'store/actions/modals'
import { createErrorNotification } from 'store/actions/notifications'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { refreshCounter } from 'store/actions/refresh'

import { Button } from 'components/Forms'
import BaseModal from 'components/Modals/BaseModal'
import useApi from 'components/Hooks/useApi'
import { t } from 'translations'

interface Props {
  payload: {
    start: string
  }
}

interface NoteResponse {
  _id: string
  note: string
}

const ScheduleNotesModal: FC<Props> = ({ payload }) => {
  const dispatch = useDispatch()
  const { start } = payload

  const { response: noteResponse, isLoading: fetchNotesLoading } = useApi<NoteResponse>({ method: 'GET', url: `schedule/notes?day=${start}` })
  const { handleApi: createScheduleNote, isError: errorCreatingNote } = useApi({ method: 'POST', url: `schedule/notes` })
  const { handleApi: updateScheduleNote, isError: errorUpdatingNote } = useApi({ method: 'PUT', url: `schedule/notes` })

  useEffect(() => {
    if (errorCreatingNote || errorUpdatingNote) {
      createErrorNotification(dispatch)
    }
  }, [errorCreatingNote, errorUpdatingNote, dispatch])

  if (fetchNotesLoading) return null

  return (
    <BaseModal title={`manager.schedule.SCHEDULE_NOTE`} hideButtons>
      <div className="mt-2">
        <Formik
          initialValues={{ note: noteResponse?.note || '' }}
          enableReinitialize
          onSubmit={async values => {
            try {
              if (noteResponse) {
                await updateScheduleNote({ id: noteResponse._id, ...values })
                return
              }
              await createScheduleNote({ day: start, ...values })
            } catch (error) {
              createErrorNotification(dispatch)
            } finally {
              dispatch(refreshCounter('scheduleNotes'))
              dispatch(closeModal())
            }
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <textarea
                  placeholder={t(`manager.schedule.SCHEDULE_NOTE.ADD_UPDATE_SCHEDULE_NOTE`)}
                  rows={10}
                  className="form-control"
                  id="note"
                  name="note"
                  onChange={handleChange}
                  value={values.note}
                />
              </div>
              <div>
                <Button text="actions.SUBMIT" className="btn btn-primary mr-2" type="submit" />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </BaseModal>
  )
}

export default ScheduleNotesModal
